/**
 * Removes hash segment from URL for comparison purposes.
 *
 * This function normalizes URLs by removing the hash segment (e.g., `/h-abc123/`)
 * that appears after the package name and version. This is useful for comparing
 * URLs that point to the same resource but with different hash values.
 *
 * The hash segment is used for cache-busting and content versioning. By removing it,
 * we can check if two URLs refer to the same logical resource regardless of their
 * specific hash values.
 *
 * @param url - URL to remove hash from
 * @returns URL with hash segment removed, or original URL if no hash segment is found
 *
 * @example
 * ```ts
 * // Remove hash from a URL
 * removeHashFromUrl('http://localhost:3000/react@17.5.0/h-abc123/v0/bundled/index.js');
 * // Returns: 'http://localhost:3000/react@17.5.0/v0/bundled/index.js'
 *
 * // URL without hash is returned unchanged
 * removeHashFromUrl('http://localhost:3000/react@17.5.0/v0/bundled/index.js');
 * // Returns: 'http://localhost:3000/react@17.5.0/v0/bundled/index.js'
 *
 * // Compare URLs with different hashes
 * const url1 = 'http://localhost:3000/pkg@1.0.0/h-old/bundled/index.js';
 * const url2 = 'http://localhost:3000/pkg@1.0.0/h-new/bundled/index.js';
 * removeHashFromUrl(url1) === removeHashFromUrl(url2); // true
 * ```
 */
export function removeHashFromUrl(url: string): string {
  // Find the start of the hash segment (e.g., '/h-abc123/')
  const hashStart = url.indexOf('/h-');
  if (hashStart === -1) {
    return url;
  }

  // Find the end of the hash segment (the next '/' after '/h-')
  const hashEnd = url.indexOf('/', hashStart + 1);
  if (hashEnd === -1) {
    return url;
  }

  // Remove the hash segment by concatenating everything before and after it
  return url.slice(0, hashStart) + url.slice(hashEnd);
}
